import moment from "moment";
import React, { useState } from "react";
import { Button, Col, Container, Nav, Row, Tab } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomPagination from "../../../components/Common/CustomPagination";
import TableActions from "../../../components/Common/TableActions";
import TableLayout from "../../../components/TableLayout";
import dataHandler from "../../../hooks/dataHandler";
import { GETBOOKING } from "../../../services/ApiCalls";
import {
  activeInactiveOptions,
  AdminStatusOptionSubscription,
  BookingStatusOptions,
  orderStatusArr,
  paidUnpaidFilter,
} from "../../../utilities/const";
import {
  capitalizedFirstAlphaBet,
  exportCsvHandler,
  findLabelOfStatus,
} from "../../../utilities/utilities";
import Filter from "../../../components/Common/Filter";
import Toggle from "../../../components/Common/Toggle";
import ImportCsv from "../../../components/Common/ImportCsv";
import ButtonLoader from "../../../components/Common/ButtonLoader";
import CountryFilter from "../../../components/Common/CountryFilter";
import AdminUserStat from "../../../components/Common/AdminUserStat";
import HotelAdminFilter from "../../../components/Common/HotelAdminFilter";

const OrderManagement = () => {
  const { t } = useTranslation();
  const [userDetails, setUserUserDetails] = useState();
  const [exportLoader, setExportLoader] = useState();
  const [deleteModal, setDeleteModel] = useState();
  const {
    setBody,
    body,
    data,
    loader,
    paginationHandler,
    searchHandler,
    total,
  } = dataHandler({
    api: GETBOOKING,
  });
  const { id } = useParams();
  const column = [
    {
      head: t("manageBooking.columns.serialNumber"),
      accessor: "#",
      component: (item, key) => body.limit * (body.page - 1) + key + 1,
    },
    {
      head: t("manageBooking.columns.bookingId"),
      accessor: "bookingId",
      component: (item) => (
        <span title={item?.bookingId || "-"}>{item?.bookingId || "-"}</span>
      ),
    },
    // {
    //   head: "Booking Type",
    //   accessor: "bookingType",
    //   component: (item) => item?.bookingType || "-",
    // },
    {
      head: t("manageBooking.columns.hotelName"),
      accessor: "hotelName",
      component: (item) => (
        <span title={item?.hotelDetail?.hotelName || "-"}>
          {item?.hotelDetail?.hotelName || "-"}
        </span>
      ),
    },
    {
      head: t("manageBooking.columns.hotelId"),
      accessor: "hotelId",
      component: (item) => (
        <span title={item?.hotelDetail?.hotelId || "-"}>
          {item?.hotelDetail?.hotelId || "-"}
        </span>
      ),
    },
    // {
    //   head: "Room No.",
    //   accessor: "roomNumber",
    //   component: (item) => (
    //     <span className="d-flex justify-content-center align-items-center">
    //       {item?.roomDetail?.roomNumber || "-"}
    //     </span>
    //   ),
    // },
    {
      head: t("manageBooking.columns.name"),
      accessor: "customerName",
      component: (item) => {
        const name = item?.customerDetail
          ? `${item.customerDetail.first_name} ${item.customerDetail.last_name}`
          : "-";
        return <span title={name}>{name}</span>;
      },
    },
    {
      head: t("manageBooking.columns.email"),
      accessor: "email",
      component: (item) => (
        <span
          title={item?.customerDetail?.email || "-"}
          className="text-nowrap d-inline-block"
          style={{
            maxWidth: "180px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item?.customerDetail?.email || "-"}
        </span>
      ),
    },
    {
      head: t("manageBooking.columns.checkIn"),
      accessor: "checkInDate",
      component: (item) => {
        const text = item?.checkInDate
          ? moment(item.checkInDate).format("MM-DD-YYYY, hh:mm A")
          : "--";
        return (
          <span
            title={text}
            className="text-nowrap d-inline-block"
            style={{
              maxWidth: "150px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      head: t("manageBooking.columns.checkOut"),
      accessor: "checkOutDate",
      component: (item) => {
        const text = item?.checkOutDate
          ? moment(item.checkOutDate).format("MM-DD-YYYY, hh:mm A")
          : "--";
        return (
          <span
            title={text}
            className="text-nowrap d-inline-block"
            style={{
              maxWidth: "150px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {text}
          </span>
        );
      },
    },

    // {
    //   head: "Guests",
    //   accessor: "numberOfGuests",
    //   component: (item) => item?.numberOfGuests || "--",
    // },
    // {
    //   head: "Nights",
    //   accessor: "nightCount",
    //   component: (item) => item?.nightCount || "--",
    // },
    // {
    //   head: "Requests",
    //   accessor: "specialRequests",
    //   component: (item) => (
    //     <span title={item?.specialRequests || "-"}>
    //       {item?.specialRequests || "-"}
    //     </span>
    //   ),
    // },
    {
      head: t("manageBooking.columns.finalPrice"),
      accessor: "finalPrice",
      component: (item) => (
        <span className="d-flex justify-content-center align-items-center">
          {`${item?.currencySymbol || "$"} ${
            item?.finalPrice?.toLocaleString() || "--"
          }`}
        </span>
      ),
    },
    {
      head: t("manageBooking.columns.paymentMethod"),
      accessor: "paymentMethod",
      component: (item) => item?.paymentMethod?.toUpperCase() || "-",
    },
    {
      head: t("manageBooking.columns.paymentStatus"),
      accessor: "paymentStatus",
      component: (item) => (
        <span
          className={`badge ${
            item?.paymentStatus?.toLowerCase() === "paid"
              ? "bg-success"
              : "bg-warning"
          }`}
        >
          {item?.paymentStatus || "--"}
        </span>
      ),
    },
    {
      head: t("manageBooking.columns.bookingStatus"),
      accessor: "bookingStatus",
      component: (item) => {
        const status = item?.bookingStatus?.toLowerCase() || "";
        const statusStyles = {
          pending: { backgroundColor: "#fff3cd", color: "#856404" },
          completed: { backgroundColor: "#d4edda", color: "#155724" },
          rejected: { backgroundColor: "#f8d7da", color: "#721c24" },
          cancelled: { backgroundColor: "#e2e3e5", color: "#383d41" },
        };
        const style = statusStyles[status] || {
          backgroundColor: "#f0f0f0",
          color: "#333",
        };
        return (
          <span
            style={{
              ...style,
              borderRadius: "12px",
              padding: "4px 10px",
              display: "inline-block",
              fontWeight: "600",
              fontSize: "0.85rem",
              textTransform: "capitalize",
            }}
          >
            {status || "--"}
          </span>
        );
      },
    },
    {
      head: t("manageBooking.columns.createdAt"),
      accessor: "createdAt",
      component: (item) =>
        item?.createdAt
          ? moment(item.createdAt).format("MM-DD-YYYY, hh:mm A")
          : "--",
    },
    {
      head: t("manageBooking.columns.action"),
      accessor: "Action",
      component: (item) => (
        <TableActions viewLink={`/manage-booking/${item._id}`} />
      ),
    },
  ];

  return (
    <>
      <section className="orderManagement position-relative py-2">
        <Container>
          <Row>
            <Col lg="12 ">
              <div className="d-flex  justify-content-between">
                <h4 className="mb-0 fw-bold text-dark text-capitalize">
                  {t("manageBooking.title")}
                </h4>
                <div className="right">
                  <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 flex-wrap">
                    <li className="">
                      <Button
                        className="d-flex align-items-center justify-content-center fw-sbold commonBtn GreyBtn"
                        style={{ height: 40, minWidth: 100, fontSize: 12 }}
                        type="button"
                        onClick={() =>
                          exportCsvHandler("bookingcsv ", setExportLoader)
                        }
                      >
                        {exportLoader ? <ButtonLoader /> : t("manageBooking.buttons.exportCsv")}
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <AdminUserStat platform={data?.platform} type="booking" />
            </Col>
            <Col lg="12" className="my-2">
              <div className="tableFilter d-flex align-items-center justify-content-between flex-wrap gap-10 mb-3">
                <div className="left">
                  <HotelAdminFilter
                  type={"bookings"}
                    body={body}
                    searchHandler={searchHandler}
                    setBody={setBody}
                    statusFilterOptionArr={activeInactiveOptions}
                  />
                </div>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <div className="my-2">
                <Col lg="12" className="my-2">
                  <TableLayout
                    column={column}
                    data={data?.resData}
                    loader={loader}
                  />
                  <CustomPagination
                    total={total}
                    pageChangeHandler={paginationHandler}
                    body={body}
                    setBody={setBody}
                  />
                </Col>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default OrderManagement;
